/*
All material copyright ESRI, All Rights Reserved, unless otherwise specified.
See https://js.arcgis.com/4.28/esri/copyright.txt for details.
*/
import { _ as e } from "../../../chunks/tslib.es6.js";
import "../../../geometry.js";
import t from "../../../Graphic.js";
import r from "../../../core/Collection.js";
import s from "../../../core/Error.js";
import has from "../../../core/has.js";
import i from "../../../core/Loadable.js";
import o from "../../../core/Logger.js";
import { EsriPromiseMixin as n } from "../../../core/Promise.js";
import { open as a } from "../../../core/workers/workers.js";
import { property as l } from "../../../core/accessorSupport/decorators/property.js";
import { ensureType as u } from "../../../core/accessorSupport/ensureType.js";
import "../../../core/arrayUtils.js";
import { shared as p } from "../../../core/accessorSupport/decorators/shared.js";
import { subclass as c } from "../../../core/accessorSupport/decorators/subclass.js";
import { unapplyEditsZUnitScaling as d } from "../../../rest/query/operations/editsZScale.js";
import { applyFeatureSetZUnitScaling as h } from "../../../rest/query/operations/queryZScale.js";
import y from "../../../rest/support/FeatureSet.js";
import m from "../../../geometry/Extent.js";
import f from "../../../geometry/Polygon.js";
import { typeKebabDictionary as g } from "../../../geometry/support/typeUtils.js";
let _ = 0,
  T = class extends i.LoadableMixin(n(r)) {
    constructor(e) {
      super(e), this._idToClientGraphic = null, this.type = "memory";
    }
    load(e) {
      const t = null != e ? e.signal : null;
      return this.addResolvingPromise(this._startWorker(t)), Promise.resolve(this);
    }
    destroy() {
      this._connection?.close(), this._connection = null;
    }
    get _workerGeometryType() {
      const e = this.layer?.geometryType;
      return e ? this._geometryTypeRequiresClientGraphicMapping(e) ? "polygon" : e : null;
    }
    applyEdits(e) {
      return this.load().then(() => this._applyEdits(e));
    }
    openPorts() {
      return this.load().then(() => this._connection.openPorts());
    }
    async queryFeatures(e, t = {}) {
      await this.load(t);
      const r = await this._connection.invoke("queryFeatures", e ? e.toJSON() : null, t);
      h(e, this.layer.spatialReference, r);
      const s = y.fromJSON(r);
      if (!this._requiresClientGraphicMapping()) return s;
      const i = this.layer.objectIdField;
      for (const o of s.features) {
        const e = o.attributes[i],
          t = this._idToClientGraphic.get(e);
        t && (o.geometry = t.geometry);
      }
      return s.geometryType = this.layer.geometryType, s;
    }
    async queryFeaturesJSON(e, t = {}) {
      if (this._requiresClientGraphicMapping()) throw new s("query-features-json:unsupported", "Cannot query in JSON format for client only geometry types (mesh and extent)");
      await this.load(t);
      const r = await this._connection.invoke("queryFeatures", e ? e.toJSON() : null, t);
      return h(e, this.layer.spatialReference, r), r;
    }
    queryFeatureCount(e, t = {}) {
      return this.load(t).then(() => this._connection.invoke("queryFeatureCount", e ? e.toJSON() : null, t));
    }
    queryObjectIds(e, t = {}) {
      return this.load(t).then(() => this._connection.invoke("queryObjectIds", e ? e.toJSON() : null, t));
    }
    queryExtent(e, t = {}) {
      return this.load(t).then(() => this._connection.invoke("queryExtent", e ? e.toJSON() : null, t)).then(e => ({
        count: e.count,
        extent: m.fromJSON(e.extent)
      }));
    }
    querySnapping(e, t = {}) {
      return this.load(t).then(() => this._connection.invoke("querySnapping", e, t));
    }
    async _applyEdits(e) {
      if (!this._connection) throw new s("feature-layer-source:edit-failure", "Memory source not loaded");
      const t = this.layer.objectIdField;
      let r = null;
      const i = [],
        o = [];
      await Promise.all([this._prepareClientMapping(e.addFeatures, null), this._prepareClientMapping(e.updateFeatures, null)]);
      const n = e => "objectId" in e && null != e.objectId ? e.objectId : "attributes" in e && null != e.attributes[t] ? e.attributes[t] : null;
      if (e.addFeatures && (r = this._prepareAddFeatures(e.addFeatures)), e.deleteFeatures) for (const s of e.deleteFeatures) {
        const e = n(s);
        null != e && i.push(e);
      }
      const a = e.updateFeatures && this._idToClientGraphic ? new Map() : null;
      if (e.updateFeatures) for (const s of e.updateFeatures) if (o.push(this._serializeFeature(s)), a) {
        const e = n(s);
        null != e && a.set(e, s);
      }
      d(r ? r.features : null, o, this.layer.spatialReference);
      const {
        fullExtent: l,
        featureEditResults: u
      } = await this._connection.invoke("applyEdits", {
        adds: r ? r.features : [],
        updates: o,
        deletes: i
      });
      return this.fullExtent = l, r && r.finish(u.uidToObjectId), this._updateClientGraphicIds(a, u), this._createEditsResult(u);
    }
    async _prepareClientMapping(e, t) {
      if ("mesh" !== this._layerOrSourceGeometryType || null == e) return;
      const r = [];
      for (const {
        geometry: s
      } of e) null == s || "mesh" !== s.type || s.hasExtent || s.loaded || r.push(s.load({
        signal: t
      }));
      r.length && (await Promise.all(r));
    }
    _updateClientGraphicIds(e, t) {
      if (this._idToClientGraphic) {
        if (e) for (const r of t.updateResults) {
          if (!r.success) continue;
          const t = e.get(r.objectId);
          null != t && this._addIdToClientGraphic(t);
        }
        for (const e of t.deleteResults) e.success && this._idToClientGraphic.delete(e.objectId);
      }
    }
    _createEditsResult(e) {
      return {
        addFeatureResults: e.addResults ? e.addResults.map(this._createFeatureEditResult, this) : [],
        updateFeatureResults: e.updateResults ? e.updateResults.map(this._createFeatureEditResult, this) : [],
        deleteFeatureResults: e.deleteResults ? e.deleteResults.map(this._createFeatureEditResult, this) : [],
        addAttachmentResults: [],
        updateAttachmentResults: [],
        deleteAttachmentResults: []
      };
    }
    _createFeatureEditResult(e) {
      const t = !0 === e.success ? null : e.error || {
        code: void 0,
        description: void 0
      };
      return {
        objectId: e.objectId,
        globalId: e.globalId,
        error: t ? new s("feature-layer-source:edit-failure", t.description, {
          code: t.code
        }) : null
      };
    }
    _prepareAddFeatures(e) {
      const t = new Map(),
        r = new Array(e.length);
      let s = null;
      for (let o = 0; o < e.length; o++) {
        const i = e[o],
          n = this._serializeFeature(i);
        s || null == i.geometry || (s = i.geometry.type), r[o] = n, t.set(`${n.uid}`, i);
      }
      const i = this;
      return {
        features: r,
        inferredGeometryType: s,
        finish(e) {
          const r = i.sourceJSON.objectIdField;
          for (const s in e) {
            const o = e[s],
              n = t.get(s);
            n && (n.attributes || (n.attributes = {}), -1 === o ? delete n.attributes[r] : n.attributes[r] = o, i._addIdToClientGraphic(n));
          }
        }
      };
    }
    _addIdToClientGraphic(e) {
      if (!this._idToClientGraphic) return;
      const t = this.sourceJSON.objectIdField,
        r = e.attributes?.[t];
      null != r && this._idToClientGraphic.set(r, e);
    }
    get _layerOrSourceGeometryType() {
      return this.layer?.geometryType ?? this.sourceJSON?.geometryType;
    }
    _requiresClientGraphicMapping() {
      return this._geometryTypeRequiresClientGraphicMapping(this._layerOrSourceGeometryType);
    }
    _geometryRequiresClientGraphicMapping(e) {
      return this._geometryTypeRequiresClientGraphicMapping(e.type);
    }
    _geometryTypeRequiresClientGraphicMapping(e) {
      return "mesh" === e || "multipatch" === e || "extent" === e;
    }
    _serializeFeature(e) {
      const {
          attributes: t
        } = e,
        r = this._geometryForSerialization(e),
        s = (_++).toString();
      return r ? {
        uid: s,
        geometry: r.toJSON(),
        attributes: t
      } : {
        uid: s,
        attributes: t
      };
    }
    _geometryForSerialization(e) {
      const {
        geometry: t
      } = e;
      if (null == t) return null;
      if (this._geometryRequiresClientGraphicMapping(t)) {
        return t.extent ? f.fromExtent(t.extent) : null;
      }
      return t;
    }
    async _startWorker(e) {
      this._connection = await a("MemorySourceWorker", {
        strategy: has("feature-layers-workers") ? "dedicated" : "local",
        signal: e,
        registryTarget: this
      });
      const {
          fields: t,
          spatialReference: r,
          objectIdField: s,
          hasM: i,
          hasZ: n,
          timeInfo: l,
          dateFieldsTimeZone: u
        } = this.layer,
        p = "defaults" === this.layer.originOf("spatialReference");
      await this._prepareClientMapping(this.items, e);
      const c = this._prepareAddFeatures(this.items);
      this.addHandles(this.on("before-changes", e => {
        o.getLogger(this).error("Source modifications will not propagate after layer has been loaded. Please use .applyEdits() instead"), e.preventDefault();
      }));
      const d = {
          features: c.features,
          fields: t?.map(e => e.toJSON()),
          geometryType: g.toJSON(this._workerGeometryType),
          hasM: "mesh" !== this._layerOrSourceGeometryType && i,
          hasZ: "mesh" === this._layerOrSourceGeometryType || n,
          objectIdField: s,
          spatialReference: p ? null : r && r.toJSON(),
          timeInfo: l?.toJSON() ?? null,
          dateFieldsTimeZone: u
        },
        h = await this._connection.invoke("load", d, {
          signal: e
        });
      for (const a of h.warnings) o.getLogger(this.layer).warn("#load()", `${a.message} (title: '${this.layer.title || "no title"}', id: '${this.layer.id ?? "no id"}')`, {
        warning: a
      });
      h.featureErrors.length && o.getLogger(this.layer).warn("#load()", `Encountered ${h.featureErrors.length} validation errors while loading features. (title: '${this.layer.title || "no title"}', id: '${this.layer.id ?? "no id"}')`, {
        errors: h.featureErrors
      });
      const y = h.layerDefinition;
      this._geometryTypeRequiresClientGraphicMapping(c.inferredGeometryType) && (y.geometryType = g.toJSON(c.inferredGeometryType)), this.sourceJSON = y, this._requiresClientGraphicMapping() && (this._idToClientGraphic = new Map()), c.finish(h.assignedObjectIds);
    }
  };
e([p({
  Type: t,
  ensureType: u(t)
})], T.prototype, "itemType", void 0), e([l()], T.prototype, "type", void 0), e([l({
  constructOnly: !0
})], T.prototype, "layer", void 0), e([l({
  readOnly: !0
})], T.prototype, "_workerGeometryType", null), e([l()], T.prototype, "sourceJSON", void 0), T = e([c("esri.layers.graphics.sources.MemorySource")], T);
export { T as MemorySource };